import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import * as Sentry from "@sentry/react";
import '../../styling/auth.css';

import { 
  AlertCircle, 
  Mail, 
  Lock, 
  Loader2, 
  ArrowRight, 
  CheckCircle, 
  XCircle,
  KeyRound,
  Key, 
  Eye,
  EyeOff,
  AlertTriangle
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'login' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'login' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'login',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Responsive utilities
const useResponsive = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isMobile, isTablet };
};

// UI-specific error messages (AuthContext handles all authentication errors)
const ERROR_MESSAGES = {
  // Rate limiting display
  'RATE_LIMIT_EXCEEDED': 'API limit reached. Try again after 60 seconds',
  
  // Client-side validation
  'VALIDATION_ERROR': 'Please check your input and try again',
  
  // Network errors
  'NETWORK_ERROR': 'Network error. Please check your connection',
  
  // Fallback
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again later.'
};

// Security: Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return ERROR_MESSAGES['SERVER_ERROR'];
  }
  
  // ✅ 1. Check backend error codes FIRST
  if (error.response?.data?.code && ERROR_MESSAGES[error.response.data.code]) {
    return ERROR_MESSAGES[error.response.data.code];
  }
  
  // ✅ 2. Check direct error code
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // ✅ 3. Check interceptor fields (for non-AuthContext errors)
  if (error.userMessage) {
    return error.userMessage;
  }
  
  if (error.error) {
    return error.error;
  }
  
  // ✅ 4. Handle HTTP status codes
  if (error.response?.status === 401 || error.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  
  // ✅ 5. Check error message (sanitized)
  if (error.message && typeof error.message === 'string') {
    return sanitizeText(error.message);
  }
  
  // ✅ 6. Fallback
  return ERROR_MESSAGES['SERVER_ERROR'];
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Email validation
const validateEmail = (email) => {
  if (!email || typeof email !== 'string') return { valid: false, error: 'Email is required' };
  const trimmedEmail = email.trim();
  if (trimmedEmail.length === 0) return { valid: false, error: 'Email is required' };
  if (trimmedEmail.length > 254) return { valid: false, error: 'Email too long' };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) return { valid: false, error: 'Please enter a valid email address' };
  return { valid: true };
};

// Security: Password validation
const validatePassword = (password) => {
  if (!password || typeof password !== 'string') return { valid: false, error: 'Password is required' };
  if (password.length < 1) return { valid: false, error: 'Password is required' };
  if (password.length > 128) return { valid: false, error: 'Password too long' };
  return { valid: true };
};

// Security: OTP validation
const validateOTP = (otp) => {
  if (!otp || typeof otp !== 'string') return { valid: false, error: 'Verification code is required' };
  const sanitizedOTP = otp.replace(/\D/g, '');
  if (sanitizedOTP.length !== 6) return { valid: false, error: 'Verification code must be 6 digits' };
  return { valid: true };
};
const validateRedirectPath = (path) => {
  // Reject if not a string or empty
  if (!path || typeof path !== 'string') {
    return '/dashboard';
  }

  const cleanPath = path.trim();

  // Must start with / (internal path only)
  if (!cleanPath.startsWith('/')) {
    return '/dashboard';
  }

  // Reject external URLs
  if (cleanPath.includes('://') || cleanPath.includes('\\')) {
    return '/dashboard';
  }

  // Reject protocol-relative URLs
  if (cleanPath.startsWith('//')) {
    return '/dashboard';
  }

  // Reject javascript: or data: URIs
  if (cleanPath.toLowerCase().startsWith('javascript:') || 
      cleanPath.toLowerCase().startsWith('data:')) {
    return '/dashboard';
  }

  // Path is safe - it's internal and not malicious
  return cleanPath;
};

const PROVIDERS = {
  EMAIL: 'email',
  GOOGLE: 'google'
};

const FRONTEND_URL = 'http://barrack.ai';

const Login = () => {
  const { isMobile, isTablet } = useResponsive();
  
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    handleEmailLogin, 
    handleSocialAuth, 
    handleOAuthCallback,
    handleSendLoginOtp,
    handleVerifyLoginOtp,
    handleResendLoginOtp,
    handleForgotPassword,
    handleResetPassword,
    handleResendVerificationOtp,
    isAuthenticated, 
    clearError 
  } = useAuth();

  // Performance: Controlled loading states
  const [loadingStates, setLoadingStates] = useState({
    emailLogin: false,
    socialLogin: false,
    otpLogin: false,
    sendOtp: false,
    forgotPassword: false,
    resetPassword: false,
    resendOtp: false
  });

  const [formState, setFormState] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
    loginOtp: ''
  });

  const [uiState, setUiState] = useState({
    currentProvider: null,
    showSuccess: false,
    passwordVisible: false,
    lastAttemptTime: null,
    attemptCount: 0,
    verificationSent: false,
    verificationEmail: '',
    resendDisabled: false,
    resendCountdown: 0,
    isOtpLoginMode: false,
    loginOtpSent: false
  });

  const [errorState, setErrorState] = useState({
    formErrors: {},
    authError: null,
    providerError: null,
    rateLimitError: null,
    otpError: null,
    loginOtpError: null
  });
  
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetPasswordMode, setResetPasswordMode] = useState(false);

  // Performance: Request cancellation
  const abortControllerRef = useRef(null);
  const loginTimeoutRef = useRef(null);
  const otpTimeoutRef = useRef(null);
  const forgotPasswordTimeoutRef = useRef(null);

  // Component mount logging
  useEffect(() => {
    logInfo('Login component mounted', {
      isAuthenticated,
      isMobile,
      isTablet,
      currentPath: location.pathname,
      hasLocationState: !!location.state
    });
  }, []);

  // Set Sentry context
  useEffect(() => {
    Sentry.setContext("login", {
      isAuthenticated,
      currentProvider: uiState.currentProvider,
      isOtpLoginMode: uiState.isOtpLoginMode,
      loginOtpSent: uiState.loginOtpSent,
      showEmailForm,
      forgotPasswordMode,
      resetPasswordMode,
      isMobile,
      isTablet,
      hasFormErrors: Object.keys(errorState.formErrors).length > 0,
      hasAuthError: !!errorState.authError,
      attemptCount: uiState.attemptCount
    });
  }, [isAuthenticated, uiState, showEmailForm, forgotPasswordMode, resetPasswordMode, 
      isMobile, isTablet, errorState]);

  useEffect(() => {
    if (location.state?.message) {
      setErrorState(prev => ({
        ...prev,
        authError: {
          code: 'SESSION_EXPIRED',
          message: location.state.message
        }
      }));
      
      logInfo('Navigation message displayed', {
        message: location.state.message,
        from: location.state.from
      });
      
      // Clear the message from location state after displaying
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.message]);

  // Performance: Memoized form validation
  const formValidation = useMemo(() => {
    return {
      email: validateEmail(formState.email),
      password: validatePassword(formState.password),
      loginOtp: validateOTP(formState.loginOtp)
    };
  }, [formState.email, formState.password, formState.loginOtp]);

  // Performance: Request cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
      }
      if (otpTimeoutRef.current) {
        clearTimeout(otpTimeoutRef.current);
      }
      if (forgotPasswordTimeoutRef.current) {
        clearTimeout(forgotPasswordTimeoutRef.current);
      }
      
      logInfo('Login component unmounted', {
        hadPendingRequests: !!(abortControllerRef.current || loginTimeoutRef.current || 
                              otpTimeoutRef.current || forgotPasswordTimeoutRef.current)
      });
    };
  }, []);

  const handleProviderSwitch = useCallback(async (provider) => {
    logUserAction('provider_switch_attempted', {
      targetProvider: provider,
      currentEmail: formState.email ? 'provided' : 'empty'
    });
    
    if (provider === PROVIDERS.EMAIL) {
      clearError();
      setErrorState({ 
        formErrors: {}, 
        authError: null, 
        providerError: null 
      });
      
      logInfo('Switched to email provider', {
        previousProvider: uiState.currentProvider
      });
    } else {
      const email = formState.email;
      navigate(`/login/${provider}`, { 
        state: { 
          email,
          from: location.pathname,
          reason: 'WRONG_PROVIDER'
        }
      });
      
      logUserAction('provider_switch_navigation', {
        targetProvider: provider,
        hasEmail: !!email,
        reason: 'WRONG_PROVIDER'
      });
    }
  }, [clearError, formState.email, location.pathname, navigate, uiState.currentProvider]);

  // Enhanced auth state handling
  useEffect(() => {
    const handleAuthState = async () => {
      const startTime = performance.now();
      
      if (isAuthenticated && !location.pathname.includes('/login')) {
const redirectPath = validateRedirectPath(location.state?.from || '/dashboard');
        
        logUserAction('authenticated_user_redirect', {
          from: location.pathname,
          to: redirectPath
        });
        
        navigate(redirectPath, { replace: true });
        return;
      }
  
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const provider = searchParams.get('provider');
      const email = searchParams.get('email') || location.state?.email;
  
      if (email) {
        setFormState(prev => ({ ...prev, email: sanitizeText(email) }));
        
        logInfo('Email pre-filled from URL/state', {
          source: searchParams.get('email') ? 'url' : 'state',
          hasEmail: !!email
        });
      }

      if (error === 'access_denied') {
        const safeMessage = 'Authentication was cancelled';
        setErrorState(prev => ({
          ...prev,
          authError: { code: 'AUTH_CANCELLED', message: safeMessage }
        }));
        
        logUserAction('oauth_cancelled', {
          provider,
          error
        });
        
        logInfo('OAuth authentication cancelled by user', {
          provider,
          error
        });
        return;
      }

      if (code && state && provider) {
        setLoadingStates(prev => ({ ...prev, socialLogin: true }));
        setUiState(prev => ({ 
          ...prev, 
          currentProvider: provider 
        }));
        
        logUserAction('oauth_callback_received', {
          provider,
          hasCode: !!code,
          hasState: !!state
        });
        
        logInfo('Processing OAuth callback', {
          provider,
          hasCode: !!code,
          hasState: !!state
        });
        
        try {
          const result = await handleOAuthCallback(code, state, provider);
          const endTime = performance.now();
          const duration = endTime - startTime;

          switch (result.status) {
            case 'SUCCESS':
              setUiState(prev => ({ ...prev, showSuccess: true }));
const redirectPath = validateRedirectPath(localStorage.getItem('auth_redirect_path') || '/dashboard');
              
              logPerformance('oauth_login', duration, {
                success: true,
                provider,
                redirectPath
              });
              
              logUserAction('oauth_login_success', {
                provider,
                redirectPath,
                duration: `${duration.toFixed(2)}ms`
              });
              
              logInfo('OAuth login completed successfully', {
                provider,
                redirectPath,
                duration: `${duration.toFixed(2)}ms`
              });
              
              setTimeout(() => navigate(redirectPath, { replace: true }), 1500);
              break;

            case 'WRONG_PROVIDER':
              setErrorState(prev => ({
                ...prev,
                authError: {
                  code: 'WRONG_PROVIDER',
                  message: result.message,
                  provider: result.provider,
                  action: result.action,
                  actionHandler: () => handleProviderSwitch(result.provider)
                }
              }));
              
              logPerformance('oauth_login', duration, {
                success: false,
                provider,
                error: 'wrong_provider',
                suggestedProvider: result.provider
              });
              
              logUserAction('oauth_wrong_provider', {
                attemptedProvider: provider,
                suggestedProvider: result.provider,
                duration: `${duration.toFixed(2)}ms`
              });
              
              logError('OAuth login failed - wrong provider', {
                attemptedProvider: provider,
                suggestedProvider: result.provider,
                duration: `${duration.toFixed(2)}ms`
              });
              break;

            default:
              throw {
                code: 'UNEXPECTED_STATUS',
                message: 'Unexpected OAuth callback status'
              };
          }
        } catch (error) {
          const endTime = performance.now();
          const duration = endTime - startTime;
          
          // AuthContext already handled and sanitized the error
          const safeErrorMessage = error.message || getSafeErrorMessage(error);
          setErrorState(prev => ({
            ...prev,
            authError: { 
              code: error.code || 'SERVER_ERROR', 
              message: safeErrorMessage 
            }
          }));
          
          logPerformance('oauth_login', duration, {
            success: false,
            provider,
            error: safeErrorMessage,
            errorCode: error.code
          });
          
          logError('OAuth login error', {
            provider,
            errorCode: error.code,
            errorMessage: error.message,
            duration: `${duration.toFixed(2)}ms`
          }, error);
          
          logUserAction('oauth_login_error', {
            provider,
            errorCode: error.code,
            duration: `${duration.toFixed(2)}ms`
          });
        } finally {
          setLoadingStates(prev => ({ ...prev, socialLogin: false }));
        }
      }
    };

    handleAuthState();
  }, [isAuthenticated, location, navigate, handleOAuthCallback, handleProviderSwitch]);

  // Countdown timer for OTP expiration
  useEffect(() => {
    let timer;
    if ((uiState.verificationSent || uiState.loginOtpSent) && uiState.resendCountdown > 0) {
      timer = setInterval(() => {
        setUiState(prev => ({
          ...prev,
          resendCountdown: prev.resendCountdown - 1,
          resendDisabled: prev.resendCountdown > 1
        }));
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [uiState.verificationSent, uiState.loginOtpSent, uiState.resendCountdown]);

  // Enhanced email login handler using AuthContext
  const handleEmailLoginSubmit = useCallback(async (e) => {
    e.preventDefault();
    const { email, password } = formState;
    
    const startTime = performance.now();
    
    logUserAction('email_login_initiated', {
      hasEmail: !!email,
      hasPassword: !!password,
      passwordLength: password.length
    });
    
    setErrorState({
      formErrors: {},
      authError: null,
      providerError: null,
      rateLimitError: null,
      loginOtpError: null
    });
    
    if (!formValidation.email.valid || !formValidation.password.valid) {
      setErrorState(prev => ({
        ...prev,
        formErrors: { form: 'Please provide both email and password to sign in.' }
      }));
      
      logUserAction('email_login_validation_failed', {
        emailValid: formValidation.email.valid,
        passwordValid: formValidation.password.valid
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, emailLogin: true }));
    setUiState(prev => ({ ...prev, currentProvider: PROVIDERS.EMAIL }));
    
    try {
      logInfo('Email login API call started', {
        email,
        provider: PROVIDERS.EMAIL
      });
      
      // AuthContext handles all error cases and returns safe error objects
      const result = await handleEmailLogin(
        sanitizeText(email.trim()), 
        password
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        setUiState(prev => ({ ...prev, showSuccess: true }));
        
        logPerformance('email_login', duration, {
          success: true,
          email: result.email,
          provider: PROVIDERS.EMAIL
        });
        
        logUserAction('email_login_success', {
          email: result.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Email login completed successfully', {
          email: result.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        setTimeout(() => {
          window.location.replace(FRONTEND_URL);
        }, 1500);
        return;
      }
      
      throw new Error(result.message || 'Login failed');
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (error.name === 'AbortError') {
        logInfo('Email login aborted by user');
        return;
      }
      
      setUiState(prev => ({ 
        ...prev, 
        attemptCount: prev.attemptCount + 1,
        lastAttemptTime: Date.now() 
      }));
      
      // AuthContext already sanitized the error, just display it
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
      } else {
        setErrorState(prev => ({
          ...prev,
          authError: {
            code: error.code || 'LOGIN_FAILED',
            message: safeMessage
          }
        }));
      }
      
      logPerformance('email_login', duration, {
        success: false,
        error: safeMessage,
        errorCode: error.code,
        email
      });
      
      logError('Email login failed', {
        errorCode: error.code,
        errorMessage: error.message,
        email,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('email_login_failed', {
        errorCode: error.code,
        duration: `${duration.toFixed(2)}ms`,
        attemptCount: uiState.attemptCount + 1
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, emailLogin: false }));
    }
  }, [formState, formValidation, uiState.attemptCount, handleEmailLogin]);

  // Send login OTP using AuthContext
  const handleSendLoginOtpSubmit = useCallback(async () => {
    const { email } = formState;
    
    const startTime = performance.now();
    
    logUserAction('send_login_otp_initiated', {
      email: email ? 'provided' : 'empty'
    });
    
    if (!formValidation.email.valid) {
      setErrorState(prev => ({
        ...prev,
        formErrors: { email: formValidation.email.error }
      }));
      
      logUserAction('send_login_otp_validation_failed', {
        validationError: formValidation.email.error
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, sendOtp: true }));
    
    try {
      logInfo('Send login OTP API call started', {
        email
      });
      
      // AuthContext handles all error cases
      const result = await handleSendLoginOtp(sanitizeText(email.trim()));
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        setUiState(prev => ({
          ...prev,
          loginOtpSent: true,
          verificationEmail: sanitizeText(email.trim()),
          resendCountdown: 60,
          resendDisabled: true,
          loginOtpError: null
        }));
        setErrorState(prev => ({
          ...prev,
          loginOtpError: null,
          authError: null,
          formErrors: {}
        }));
        
        logPerformance('send_login_otp', duration, {
          success: true,
          email
        });
        
        logUserAction('send_login_otp_success', {
          email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Login OTP sent successfully', {
          email,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        throw result;
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (error.name === 'AbortError') {
        logInfo('Send login OTP aborted by user');
        return;
      }
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
      } else {
        setErrorState(prev => ({
          ...prev,
          loginOtpError: { message: safeMessage }
        }));
      }
      
      logPerformance('send_login_otp', duration, {
        success: false,
        error: safeMessage,
        errorCode: error.code,
        email
      });
      
      logError('Send login OTP failed', {
        errorCode: error.code,
        email,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('send_login_otp_failed', {
        errorCode: error.code,
        duration: `${duration.toFixed(2)}ms`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, sendOtp: false }));
    }
  }, [formState.email, formValidation.email, handleSendLoginOtp]);

  // Enhanced OTP login handler using AuthContext
  const handleLoginWithOtp = useCallback(async (e) => {
    e.preventDefault();
    const { email, loginOtp } = formState;
    
    const startTime = performance.now();
    
    logUserAction('otp_login_initiated', {
      email: email ? 'provided' : 'empty',
      otpLength: loginOtp.length
    });
    
    setErrorState({
      formErrors: {},
      authError: null,
      providerError: null,
      rateLimitError: null,
      loginOtpError: null
    });
    
    if (!formValidation.loginOtp.valid) {
      setErrorState(prev => ({
        ...prev,
        authError: { 
          code: 'VALIDATION_ERROR',
          message: formValidation.loginOtp.error
        }
      }));
      
      logUserAction('otp_login_validation_failed', {
        validationError: formValidation.loginOtp.error,
        otpLength: loginOtp.length
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, otpLogin: true }));
    
    try {
      logInfo('OTP login API call started', {
        email,
        otpLength: loginOtp.length
      });
      
      // AuthContext handles all error cases
      const result = await handleVerifyLoginOtp(
        sanitizeText(email.trim()), 
        sanitizeText(loginOtp)
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        setUiState(prev => ({ ...prev, showSuccess: true }));
        
        logPerformance('otp_login', duration, {
          success: true,
          email: result.email
        });
        
        logUserAction('otp_login_success', {
          email: result.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('OTP login completed successfully', {
          email: result.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        setTimeout(() => {
          window.location.replace(FRONTEND_URL);
        }, 1500);
      } else {
        throw result;
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (error.name === 'AbortError') {
        logInfo('OTP login aborted by user');
        return;
      }
      
      setUiState(prev => ({ 
        ...prev, 
        attemptCount: prev.attemptCount + 1,
        lastAttemptTime: Date.now() 
      }));
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
      } else {
        setErrorState(prev => ({
          ...prev,
          authError: {
            code: error.code || 'LOGIN_FAILED',
            message: safeMessage
          }
        }));
      }
      
      logPerformance('otp_login', duration, {
        success: false,
        error: safeMessage,
        errorCode: error.code,
        email
      });
      
      logError('OTP login failed', {
        errorCode: error.code,
        email,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('otp_login_failed', {
        errorCode: error.code,
        duration: `${duration.toFixed(2)}ms`,
        attemptCount: uiState.attemptCount + 1
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, otpLogin: false }));
    }
  }, [formState, formValidation.loginOtp, uiState.attemptCount, handleVerifyLoginOtp]);

  const handleResendLoginOtpSubmit = useCallback(async () => {
    if (uiState.resendDisabled) return;
    
    const startTime = performance.now();
    
    logUserAction('resend_login_otp_initiated', {
      email: formState.email
    });
    
    setLoadingStates(prev => ({ ...prev, resendOtp: true }));
    setUiState(prev => ({ ...prev, resendDisabled: true }));
    
    try {
      logInfo('Resend login OTP API call started', {
        email: formState.email
      });
      
      // AuthContext handles all error cases
      const result = await handleResendLoginOtp(sanitizeText(formState.email.trim()));
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        setUiState(prev => ({
          ...prev,
          resendCountdown: 60,
          resendDisabled: true
        }));
        
        logPerformance('resend_login_otp', duration, {
          success: true,
          email: formState.email
        });
        
        logUserAction('resend_login_otp_success', {
          email: formState.email,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Login OTP resent successfully', {
          email: formState.email,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        throw result;
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
        
        logPerformance('resend_login_otp', duration, {
          success: false,
          error: safeMessage,
          errorCode: error.code
        });
        
        logError('Resend login OTP rate limited', {
          email: formState.email,
          duration: `${duration.toFixed(2)}ms`
        }, error);
        
        logUserAction('resend_login_otp_rate_limited', {
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        setErrorState(prev => ({
          ...prev,
          loginOtpError: { message: safeMessage }
        }));
        
        logPerformance('resend_login_otp', duration, {
          success: false,
          error: safeMessage,
          errorName: error.name
        });
        
        logError('Resend login OTP error', {
          email: formState.email,
          errorName: error.name,
          errorMessage: error.message,
          duration: `${duration.toFixed(2)}ms`
        }, error);
        
        logUserAction('resend_login_otp_error', {
          errorName: error.name,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, resendOtp: false }));
    }
  }, [formState.email, uiState.resendDisabled, handleResendLoginOtp]);

  // Enhanced social login handler using AuthContext
  const handleSocialLogin = useCallback(async (provider) => {
    const startTime = performance.now();
    
    logUserAction('social_login_initiated', {
      provider
    });
    
    setLoadingStates(prev => ({ ...prev, socialLogin: true }));
    setUiState(prev => ({ ...prev, currentProvider: provider }));
    
    try {
      setErrorState({
        formErrors: {},
        authError: null,
        providerError: null,
        rateLimitError: null,
        loginOtpError: null
      });
      
      localStorage.setItem('auth_intent', 'login');
      
      logInfo('Social login API call started', {
        provider
      });
      
      // AuthContext handles all error cases
      const result = await handleSocialAuth(provider, 'login');
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('social_login_init', duration, {
        success: true,
        provider,
        hasAuthUrl: !!result.authUrl
      });
      
      logUserAction('social_login_redirect', {
        provider,
        duration: `${duration.toFixed(2)}ms`
      });
      
      logInfo('Social login initialized successfully', {
        provider,
        duration: `${duration.toFixed(2)}ms`
      });
      
      window.location.href = result.authUrl;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
      } else {
        setErrorState(prev => ({
          ...prev,
          authError: {
            code: error.code || 'SERVER_ERROR',
            message: safeMessage
          }
        }));
      }
      
      setLoadingStates(prev => ({ ...prev, socialLogin: false }));
      
      logPerformance('social_login_init', duration, {
        success: false,
        provider,
        error: safeMessage,
        errorCode: error.code
      });
      
      logError('Social login initialization failed', {
        provider,
        errorCode: error.code,
        errorMessage: error.message,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('social_login_error', {
        provider,
        errorCode: error.code,
        duration: `${duration.toFixed(2)}ms`
      });
    }
  }, [handleSocialAuth]);

  // Handle forgot password using AuthContext
  const handleForgotPasswordSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const startTime = performance.now();
    
    logUserAction('forgot_password_initiated', {
      email: formState.email ? 'provided' : 'empty'
    });
    
    setErrorState({ formErrors: {}, authError: null, providerError: null });
    
    if (!formValidation.email.valid) {
      setErrorState(prev => ({
        ...prev,
        formErrors: { email: formValidation.email.error }
      }));
      
      logUserAction('forgot_password_validation_failed', {
        validationError: formValidation.email.error
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, forgotPassword: true }));
    
    try {
      logInfo('Forgot password API call started', {
        email: formState.email
      });
      
      // AuthContext handles all error cases
      const result = await handleForgotPassword(sanitizeText(formState.email.trim()));
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        if (result.status === 'VERIFICATION_NEEDED') {
          setUiState(prev => ({
            ...prev,
            verificationSent: true,
            verificationEmail: sanitizeText(formState.email.trim()),
            resendCountdown: 60,
            resendDisabled: true
          }));
          setResetPasswordMode(true);
          
          logPerformance('forgot_password', duration, {
            success: true,
            email: formState.email,
            status: result.status
          });
          
          logUserAction('forgot_password_success', {
            email: formState.email,
            duration: `${duration.toFixed(2)}ms`
          });
          
          logInfo('Forgot password completed successfully', {
            email: formState.email,
            duration: `${duration.toFixed(2)}ms`
          });
        }
      } else {
        throw result;
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (error.name === 'AbortError') {
        logInfo('Forgot password aborted by user');
        return;
      }
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
      } else {
        setErrorState(prev => ({
          ...prev,
          authError: {
            code: error.code || 'FORGOT_PASSWORD_FAILED',
            message: safeMessage
          }
        }));
      }
      
      logPerformance('forgot_password', duration, {
        success: false,
        error: safeMessage,
        errorCode: error.code,
        email: formState.email
      });
      
      logError('Forgot password failed', {
        errorCode: error.code,
        email: formState.email,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('forgot_password_failed', {
        errorCode: error.code,
        duration: `${duration.toFixed(2)}ms`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, forgotPassword: false }));
    }
  }, [formState.email, formValidation.email, handleForgotPassword]);

  // Handle reset password verification using AuthContext
  const handleResetPasswordSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const startTime = performance.now();
    
    logUserAction('reset_password_initiated', {
      email: uiState.verificationEmail,
      hasVerificationCode: !!formState.verificationCode,
      hasPassword: !!formState.password,
      hasConfirmPassword: !!formState.confirmPassword
    });
    
    setErrorState(prev => ({ ...prev, otpError: null }));
    
    if (!formState.verificationCode) {
      setErrorState(prev => ({
        ...prev,
        otpError: { message: 'Please enter the verification code' }
      }));
      
      logUserAction('reset_password_validation_failed', {
        error: 'missing_verification_code'
      });
      return;
    }
    
    if (!formState.password) {
      setErrorState(prev => ({
        ...prev,
        otpError: { message: 'Please enter a new password' }
      }));
      
      logUserAction('reset_password_validation_failed', {
        error: 'missing_password'
      });
      return;
    }
    
    if (formState.password !== formState.confirmPassword) {
      setErrorState(prev => ({
        ...prev,
        otpError: { message: 'Passwords do not match' }
      }));
      
      logUserAction('reset_password_validation_failed', {
        error: 'passwords_mismatch'
      });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    abortControllerRef.current = new AbortController();
    setLoadingStates(prev => ({ ...prev, resetPassword: true }));
    
    try {
      logInfo('Reset password API call started', {
        email: uiState.verificationEmail
      });
      
      // AuthContext handles all error cases
      const result = await handleResetPassword(
        sanitizeText(uiState.verificationEmail),
        sanitizeText(formState.verificationCode),
        formState.password
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        if (result.autoLogin) {
          setUiState(prev => ({ ...prev, showSuccess: true }));
          
          logPerformance('reset_password', duration, {
            success: true,
            email: uiState.verificationEmail,
            autoLogin: true
          });
          
          logUserAction('reset_password_success_auto_login', {
            email: uiState.verificationEmail,
            duration: `${duration.toFixed(2)}ms`
          });
          
          logInfo('Password reset with auto-login completed', {
            email: uiState.verificationEmail,
            duration: `${duration.toFixed(2)}ms`
          });
          
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 1500);
        } else {
          setUiState(prev => ({ ...prev, showSuccess: true }));
          
          logPerformance('reset_password', duration, {
            success: true,
            email: uiState.verificationEmail,
            autoLogin: false
          });
          
          logUserAction('reset_password_success', {
            email: uiState.verificationEmail,
            duration: `${duration.toFixed(2)}ms`
          });
          
          logInfo('Password reset completed successfully', {
            email: uiState.verificationEmail,
            duration: `${duration.toFixed(2)}ms`
          });
          
          setTimeout(() => {
            setForgotPasswordMode(false);
            setResetPasswordMode(false);
            setFormState({
              email: '',
              password: '',
              confirmPassword: '',
              verificationCode: '',
              loginOtp: ''
            });
          }, 1500);
        }
      } else {
        throw result;
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (error.name === 'AbortError') {
        logInfo('Reset password aborted by user');
        return;
      }
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      if (error.code === 'RATE_LIMIT_EXCEEDED') {
        setErrorState(prev => ({
          ...prev,
          rateLimitError: {
            code: error.code,
            message: safeMessage,
            retryTime: error.retry_time
          }
        }));
      } else {
        setErrorState(prev => ({
          ...prev,
          otpError: {
            code: error.code || 'RESET_FAILED',
            message: safeMessage
          }
        }));
      }
      
      logPerformance('reset_password', duration, {
        success: false,
        error: safeMessage,
        errorCode: error.code,
        email: uiState.verificationEmail
      });
      
      logError('Reset password failed', {
        errorCode: error.code,
        email: uiState.verificationEmail,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('reset_password_failed', {
        errorCode: error.code,
        duration: `${duration.toFixed(2)}ms`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, resetPassword: false }));
    }
  }, [formState, uiState.verificationEmail, navigate, handleResetPassword]);

  // Handler for resending verification code using AuthContext
  const handleResendCode = useCallback(async () => {
    if (uiState.resendDisabled) return;
    
    const startTime = performance.now();
    
    logUserAction('resend_verification_initiated', {
      email: uiState.verificationEmail
    });
    
    setLoadingStates(prev => ({ ...prev, resendOtp: true }));
    setUiState(prev => ({ ...prev, resendDisabled: true }));
    
    try {
      logInfo('Resend verification API call started', {
        email: uiState.verificationEmail
      });
      
      // AuthContext handles all error cases
      const result = await handleResendVerificationOtp(
        sanitizeText(uiState.verificationEmail), 
        formState.password
      );
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (result.success) {
        setUiState(prev => ({
          ...prev,
          resendCountdown: 60,
          resendDisabled: true
        }));
        
        setFormState(prev => ({
          ...prev,
          verificationCode: ''
        }));
        
        logPerformance('resend_verification', duration, {
          success: true,
          email: uiState.verificationEmail
        });
        
        logUserAction('resend_verification_success', {
          email: uiState.verificationEmail,
          duration: `${duration.toFixed(2)}ms`
        });
        
        logInfo('Verification email resent successfully', {
          email: uiState.verificationEmail,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        throw result;
      }
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // AuthContext already sanitized the error
      const safeMessage = error.message || getSafeErrorMessage(error);
      
      logPerformance('resend_verification', duration, {
        success: false,
        error: safeMessage,
        errorName: error.name
      });
      
      logError('Resend verification failed', {
        email: uiState.verificationEmail,
        error: safeMessage,
        duration: `${duration.toFixed(2)}ms`
      }, error);
      
      logUserAction('resend_verification_failed', {
        error: safeMessage,
        duration: `${duration.toFixed(2)}ms`
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, resendOtp: false }));
    }
  }, [uiState.verificationEmail, uiState.resendDisabled, formState.password, handleResendVerificationOtp]);

const toggleOtpLoginMode = useCallback(() => {
  const isCurrentlyOtpMode = uiState.isOtpLoginMode;
  
  setUiState(prev => ({
    ...prev,
    isOtpLoginMode: !prev.isOtpLoginMode,
    // DON'T reset loginOtpSent, resendCountdown, or resendDisabled
  }));
  
  setFormState(prev => ({
    ...prev,
    loginOtp: isCurrentlyOtpMode ? '' : prev.loginOtp,  // Only clear when leaving OTP mode
    password: !isCurrentlyOtpMode ? '' : prev.password  // Only clear when leaving password mode
  }));
  
  setErrorState(prev => ({
    ...prev,
    loginOtpError: null,
    authError: null,
    formErrors: {}
  }));
  
  logUserAction('otp_login_mode_toggled', {
    previousMode: isCurrentlyOtpMode ? 'otp' : 'password',
    newMode: !isCurrentlyOtpMode ? 'otp' : 'password',
    otpAlreadySent: uiState.loginOtpSent,
    remainingCooldown: uiState.resendCountdown
  });
  
  logInfo('Login mode toggled', {
    from: isCurrentlyOtpMode ? 'otp' : 'password',
    to: !isCurrentlyOtpMode ? 'otp' : 'password',
    preservedOtpState: uiState.loginOtpSent
  });
}, [uiState.isOtpLoginMode, uiState.loginOtpSent, uiState.resendCountdown]);  // Performance: Optimized input handlers
  const handleInputChange = useCallback((field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    setErrorState(prev => ({
      ...prev,
      authError: null,
      providerError: null,
      formErrors: { ...prev.formErrors, [field]: null }
    }));
    
    logUserAction('form_field_changed', {
      field,
      hasValue: !!value,
      valueLength: typeof value === 'string' ? value.length : 0
    });
  }, []);

  const handleShowEmailForm = useCallback(() => {
    setShowEmailForm(true);
    
    logUserAction('email_form_shown', {
      previousState: 'provider_selection'
    });
    
    logInfo('Email login form displayed', {
      isMobile,
      isTablet
    });
  }, [isMobile, isTablet]);

  const handleBackToProviders = useCallback(() => {
    setShowEmailForm(false);
    
    logUserAction('back_to_providers', {
      fromEmailForm: true
    });
    
    logInfo('Returned to provider selection', {
      hadFormData: !!(formState.email || formState.password)
    });
  }, [formState.email, formState.password]);

  const handleForgotPasswordToggle = useCallback((enabled) => {
    setForgotPasswordMode(enabled);
    
    logUserAction('forgot_password_mode_toggled', {
      enabled,
      hasEmail: !!formState.email
    });
    
    logInfo('Forgot password mode toggled', {
      enabled,
      hasEmail: !!formState.email
    });
  }, [formState.email]);

  const renderError = useCallback(() => {
    if (errorState.authError) {
      return (
        <div className="auth-error-container">
          <div className="auth-error-header">
            <AlertCircle size={18} />
            <span>Error</span>
          </div>
          <p>{sanitizeText(errorState.authError.message)}</p>
        </div>
      );
    }
    
    if (errorState.rateLimitError) {
      return (
        <div className="auth-error-container">
          <div className="auth-error-header">
            <AlertCircle size={18} />
            <span>Rate Limited</span>
          </div>
          <p>{sanitizeText(errorState.rateLimitError.message)}</p>
        </div>
      );
    }
    
    return null;
  }, [errorState.authError, errorState.rateLimitError]);

  return (
    <div className={`auth-container centered ${isMobile ? 'mobile' : isTablet ? 'tablet' : ''}`}>
      <div className="auth-main">
        {forgotPasswordMode && !resetPasswordMode && (
          <div className="auth-signup-container email-form">
            <div className="auth-header">
              <h2 className="auth-title">Forgot Password</h2>
              <p className="auth-subtitle">
                Enter your email address to reset your password
              </p>
            </div>
            {renderError()}

            <form onSubmit={handleForgotPasswordSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  type="email"
                  required
                  className={`input-field ${errorState.formErrors.email ? 'error' : ''}`}
                  placeholder="name@company.com"
                  value={formState.email}
                  onChange={(e) => handleInputChange('email', e.target.value.trim())}
                  maxLength={254}
                />
                {errorState.formErrors.email && (
                  <span className="field-error">{sanitizeText(errorState.formErrors.email)}</span>
                )}
              </div>

              <button type="submit" disabled={loadingStates.forgotPassword} className="submit-button">
                {loadingStates.forgotPassword ? (
                  <>
                    <Loader2 size={20} className="spinner" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Reset Password</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <button type="button" onClick={() => handleForgotPasswordToggle(false)} className="auth-link back-link">
              ← Back to login
            </button>
          </div>
        )}

        {forgotPasswordMode && resetPasswordMode && (
          <div className="auth-signup-container">
            <div className="auth-header">
              <h2 className="auth-title">Reset Your Password</h2>
              <p className="auth-subtitle">
                We sent a verification code to {sanitizeText(uiState.verificationEmail)}
              </p>
            </div>

            {errorState.otpError && (
              <div className="auth-error-container">
                <div className="auth-error-header">
                  <AlertCircle size={18} />
                  <span>Verification Failed</span>
                </div>
                <p>{sanitizeText(errorState.otpError.message)}</p>
              </div>
            )}
            
            {renderError()}

            <form onSubmit={handleResetPasswordSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  id="verificationCode"
                  type="text"
                  required
                  className="input-field"
                  placeholder="Enter 6-digit code"
                  value={formState.verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    handleInputChange('verificationCode', value);
                  }}
                  maxLength={6}
                />
                <div className="expiry-notice">
                  This code will expire in 10 minutes
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <div className="password-field">
                  <input
                    id="password"
                    type={uiState.passwordVisible ? 'text' : 'password'}
                    required
                    className="input-field"
                    placeholder="Create a new password"
                    value={formState.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    maxLength={128}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => {
                      const newVisible = !uiState.passwordVisible;
                      setUiState(prev => ({
                        ...prev,
                        passwordVisible: newVisible
                      }));
                      
                      logUserAction('password_visibility_toggled', {
                        visible: newVisible,
                        field: 'new_password'
                      });
                    }}
                    aria-label={uiState.passwordVisible ? "Hide password" : "Show password"}
                  >
                    {uiState.passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm password</label>
                <input
                  id="confirmPassword"
                  type={uiState.passwordVisible ? 'text' : 'password'}
                  required
                  className="input-field"
                  placeholder="Confirm your new password"
                  value={formState.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  maxLength={128}
                />
              </div>

              <div className="resend-container">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={uiState.resendDisabled || loadingStates.resendOtp}
                  className={`resend-button ${uiState.resendDisabled ? 'disabled' : ''}`}
                >
                  {loadingStates.resendOtp ? 'Sending...' :
                   uiState.resendDisabled 
                    ? `Resend code (${uiState.resendCountdown}s)` 
                    : 'Resend code'}
                </button>
              </div>

              <button 
                type="submit" 
                disabled={loadingStates.resetPassword} 
                className="submit-button"
              >
                {loadingStates.resetPassword ? (
                  <>
                    <Loader2 size={20} className="spinner" />
                    <span>Resetting...</span>
                  </>
                ) : (
                  <>
                    <span>Reset Password</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {!forgotPasswordMode && (
          <div className={`auth-signup-container ${showEmailForm ? 'email-form fade-in' : ''}`}>
            <div className="auth-header">
              <h1 className="auth-title">Welcome back</h1>
              <p className="auth-subtitle">Sign in to your account</p>
            </div>
     
            {renderError()}
     
            {!showEmailForm ? (
              <>
                <div className="social-buttons">
                 <button
  onClick={() => handleSocialLogin(PROVIDERS.GOOGLE)}
  disabled={loadingStates.socialLogin}
  className="social-button"
>
  {loadingStates.socialLogin && uiState.currentProvider === PROVIDERS.GOOGLE ? (
    <Loader2 size={20} className="spinner" />
  ) : (
    <FcGoogle size={20} />
  )}
  <span>Continue with Google</span>
</button>
                </div>
                
                <div className="divider">
                  <span>or</span>
                </div>
     
                <button onClick={handleShowEmailForm} className="auth-text-button">
                  Sign in with email
                </button>
     
                <div className="auth-footer">
                  Don't have an account?
                  <button type="button" onClick={() => {
                    logUserAction('signup_link_clicked', { from: 'login_page' });
                    navigate('/signup');
                  }} className="auth-link">
                    Sign up
                  </button>
                </div>
              </>
            ) : (
              <>
                <form onSubmit={uiState.isOtpLoginMode ? handleLoginWithOtp : handleEmailLoginSubmit} className="auth-form">
                  <div className="form-group">
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      className={`input-field ${errorState.formErrors.email ? 'error' : ''}`}
                      placeholder="name@company.com"
                      value={formState.email}
                      onChange={(e) => handleInputChange('email', e.target.value.trim())}
                      maxLength={254}
                    />
                    {errorState.formErrors.email && (
                      <span className="field-error">{sanitizeText(errorState.formErrors.email)}</span>
                    )}
                  </div>
                  
                  {!uiState.isOtpLoginMode ? (
                    <>
                      <div className="form-group">
                        <div className="password-header">
                          <label htmlFor="password">Password</label>
                          <div className="password-actions">
                            <button
                              type="button"
                              onClick={() => handleForgotPasswordToggle(true)}
                              className="forgot-password"
                            >
                              Forgot password?
                            </button>
                            <button
                              type="button"
                              onClick={toggleOtpLoginMode}
                              className="use-otp-button"
                            >
                              Use OTP instead
                            </button>
                          </div>
                        </div>
                        <div className="password-field">
                          <input
                            id="password"
                            type={uiState.passwordVisible ? 'text' : 'password'}
                            required
                            autoComplete="current-password"
                            className={`input-field ${errorState.formErrors.password ? 'error' : ''}`}
                            placeholder="Enter your password"
                            value={formState.password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            maxLength={128}
                          />
                          <button
                            type="button"
                            className="password-toggle"
                            onClick={() => {
                              const newVisible = !uiState.passwordVisible;
                              setUiState(prev => ({
                                ...prev,
                                passwordVisible: newVisible
                              }));
                              
                              logUserAction('password_visibility_toggled', {
                                visible: newVisible,
                                field: 'login_password'
                              });
                            }}
                            aria-label={uiState.passwordVisible ? "Hide password" : "Show password"}
                          >
                            {uiState.passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {errorState.formErrors.password && (
                          <span className="field-error">{sanitizeText(errorState.formErrors.password)}</span>
                        )}
                      </div>
                      
                      <button type="submit" disabled={loadingStates.emailLogin} className="submit-button">
                        {loadingStates.emailLogin && uiState.currentProvider === PROVIDERS.EMAIL ? (
                          <>
                            <Loader2 size={20} className="spinner" />
                            <span>Signing in...</span>
                          </>
                        ) : (
                          <>
                            <span>Sign in</span>
                            <ArrowRight size={18} />
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="form-group">
                        <div className="password-header">
                          <label htmlFor="loginOtp">Verification Code</label>
                          <button
                            type="button"
                            onClick={toggleOtpLoginMode}
                            className="use-password-button"
                          >
                            Use password instead
                          </button>
                        </div>
                        
                        {!uiState.loginOtpSent ? (
                          <div className="otp-request-container">
                            <button 
                              type="button" 
                              onClick={handleSendLoginOtpSubmit} 
                              disabled={loadingStates.sendOtp}
                              className="send-otp-button"
                            >
                              {loadingStates.sendOtp ? (
                                <>
                                  <Loader2 size={20} className="spinner" />
                                  <span>Sending...</span>
                                </>
                              ) : (
                                <>
                                  <span>Send verification code</span>
                                  <Mail size={18} />
                                </>
                              )}
                            </button>
                          </div>
                        ) : (
                          <>
                            <input
                              id="loginOtp"
                              type="text"
                              required
                              className="input-field otp-input"
                              placeholder="Enter 6-digit code"
                              value={formState.loginOtp}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                handleInputChange('loginOtp', value);
                              }}
                              maxLength={6}
                            />
                            
                            <div className="resend-container">
                              <button
                                type="button"
                                onClick={handleResendLoginOtpSubmit}
                                disabled={uiState.resendDisabled || loadingStates.resendOtp}
                                className={`resend-button ${uiState.resendDisabled ? 'disabled' : ''}`}
                              >
                                {loadingStates.resendOtp ? 'Sending...' :
                                 uiState.resendDisabled 
                                  ? `Resend code (${uiState.resendCountdown}s)` 
                                  : 'Resend code'}
                              </button>
                            </div>
                            
                            <button type="submit" disabled={loadingStates.otpLogin} className="submit-button">
                              {loadingStates.otpLogin ? (
                                <>
                                  <Loader2 size={20} className="spinner" />
                                  <span>Verifying...</span>
                                </>
                              ) : (
                                <>
                                  <span>Sign in with OTP</span>
                                  <Key size={18} />
                                </>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                  
                  {errorState.formErrors.form && (
                    <div className="form-error">
                      {sanitizeText(errorState.formErrors.form)}
                    </div>
                  )}
                </form>
     
                <button type="button" onClick={handleBackToProviders} className="auth-link back-link">
                  ← Back to login options
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
  
export default Login;